import { useEffect, useState } from 'react';
import Web3 from 'web3';
import { queryRoutes as routes } from '../constants/routes';
import { useQuery } from './query';
import { Transaction, TransactionType } from '../types/transaction';
import { TokensType } from '../types/tokens';

const web3 = new Web3();

const toBN = (value?: string) => {
  try {
    if (!value) return web3.utils.toBN(0);
    return web3.utils.toBN(value);
  } catch (error) {
    console.error('Failed to convert value to BN:', value, error);
    return web3.utils.toBN(0);
  }
};

export const useGetAccount = (address: string) => {
  const [transactions, setTransactions] = useState<Array<TransactionType>>();
  const [tokens, setTokens] = useState<TokensType[]>();
  const [isFetch, setFetch] = useState(true);
  const [lastCursor, setLastCursor] = useState<string | null>(null);
  const [hasMoreData, setHasMoreData] = useState(true);
  const {
    data: response, error, url, query,
  } = useQuery({ path: routes.account, params: { id: address, limit: 15 } });

  const { data: tokensData } = useQuery({ path: routes.account, params: { id: `${address.toLowerCase()}/tokens/owner` } });

  // Reset pagination state when address changes
  useEffect(() => {
    setLastCursor(null);
    setHasMoreData(true);
    setTransactions(undefined);
  }, [address]);

  const fetchMore = async () => {
    const nextCursor = response?.transactions?.paging?.nextCursor;
    if (nextCursor && isFetch && hasMoreData) {
      // Prevent infinite loop: if cursor is the same as last one, stop fetching
      if (nextCursor === lastCursor) {
        setHasMoreData(false);
        return;
      }
      setFetch(false);
      setLastCursor(nextCursor);
      url.searchParams.set('cursor', nextCursor);
      await query();
      setFetch(true);
    }
  };

  useEffect(() => {
    setTokens(tokensData?.data);
  }, [tokensData]);

  useEffect(() => {
    const actualData = response?.transactions?.data;

    // If no data returned but hasMore is true, stop fetching to prevent infinite loop
    if (!actualData || !actualData.length) {
      if (response?.transactions?.paging?.hasMore) {
        setHasMoreData(false);
      }
      return;
    }
    const newData = transactions || [];
    const normalizedAddress = address.toLowerCase();
    actualData.forEach((tx: TransactionType) => {
      if (!tx?.hash) return;
      if (transactions?.find((t) => t?.hash === tx.hash)) {
        return;
      }
      const normalizedTx = { ...tx };

      if (tx.nativeTransfers?.length) {
        const relevantTransfer = tx.nativeTransfers.find(
          (transfer) => transfer.from?.toLowerCase() === normalizedAddress || transfer.to?.toLowerCase() === normalizedAddress,
        );

        if (relevantTransfer) {
          // Use the enriched from/to from backend, or fallback to native transfer data
          normalizedTx.from = tx.from || relevantTransfer.from;
          normalizedTx.to = tx.to || relevantTransfer.to;
        }

        const incomingWei = tx.nativeTransfers
          .filter((transfer) => transfer.to?.toLowerCase() === normalizedAddress)
          .reduce((acc, transfer) => acc.add(toBN(transfer.valueWei)), web3.utils.toBN(0));

        const outgoingWei = tx.nativeTransfers
          .filter((transfer) => transfer.from?.toLowerCase() === normalizedAddress)
          .reduce((acc, transfer) => acc.add(toBN(transfer.valueWei)), web3.utils.toBN(0));

        if (incomingWei.gt(outgoingWei)) {
          const net = incomingWei.sub(outgoingWei);
          normalizedTx.value = web3.utils.fromWei(net);
        } else if (outgoingWei.gt(incomingWei)) {
          const net = outgoingWei.sub(incomingWei);
          normalizedTx.value = `-${web3.utils.fromWei(net)}`;
        } else if (incomingWei.gt(web3.utils.toBN(0))) {
          normalizedTx.value = web3.utils.fromWei(incomingWei);
        }
      }

      newData.push(normalizedTx);
    });
    setTransactions(newData);
  }, [response?.transactions?.paging?.nextCursor]);

  return {
    data: {
      ...response, tokens, transactions: transactions || [], countTx: response?.transactions?.countTx,
    },
    error,
    fetchMore,
    hasMore: (response?.transactions?.paging.hasMore && isFetch && hasMoreData),
  };
};

export const useGetToken = (address: string) => {
  const [isFetch, setFetch] = useState(true);
  const [token, setToken] = useState<TokensType>();
  const [transactions, setTransactions] = useState<Transaction[]>();

  const {
    data: response, error, url, query,
  } = useQuery({ path: '/api/token', params: { id: `${address.toLowerCase()}/tx`, limit: 15, parameters: ['decodeInput'] } });
  const {
    data: tokenData,
  } = useQuery({ path: '/api/token', params: { id: `${address.toLowerCase()}` } });

  const fetchMore = async () => {
    if (response?.paging?.nextCursor && isFetch) {
      setFetch(false);
      url.searchParams.set('cursor', response?.paging.nextCursor);
      await query();
      setFetch(true);
    }
  };

  useEffect(() => {
    if (!tokenData) return;
    setToken(tokenData);
  }, [tokenData]);

  useEffect(() => {
    const actualData = response?.data;
    if (!actualData || !actualData.length) return;
    const newData = transactions || [];
    actualData.forEach((tx: Transaction) => {
      if (!tx?.hash) return;
      if (transactions?.find((t) => t?.hash === tx.hash)) {
        return;
      }
      newData.push(tx);
    });
    setTransactions(newData);
  }, [response?.paging?.nextCursor]);

  return {
    data: {
      token,
      transactions: transactions || [],
      countTx: response?.count,
    },
    error,
    fetchMore,
    hasMore: (response?.paging.hasMore && isFetch),
  };
};
